"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSessionState = exports.useIsMobile = exports.MobileStyles = exports.MOBILE_CSS = exports.MOBILE_BREAKPOINT = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
/**
 * Phone-friendly layout for apps built almost entirely from inline `style={{}}` (the dashboard has
 * ~9k of them). Inline styles can't carry media queries, so this is one global stylesheet that
 * only kicks in below MOBILE_BREAKPOINT and reins in the desktop layout for every page at once:
 *
 * - the page itself never scrolls sideways;
 * - a plain <table> scrolls sideways INSIDE its own card (so white-on-dark text can't spill out
 *   of its dark card onto the light page behind it — the white-on-white bug, 2026-09-27);
 * - a <table className="stack-mobile"> turns each row into a card instead (give each <td> a
 *   `data-label="Price"` so the card shows "Price  $20");
 * - fixed-column grids stack into one column (grids using auto-fill/auto-fit/auto, and 7-day
 *   calendar grids, are left alone — add `data-keep-grid` to opt any other grid out);
 * - fixed-width boxes, images and inputs shrink to fit; inputs use 16px text so iOS doesn't zoom;
 * - buttons/selects/inputs are at least 44px tall so they're easy to tap.
 *
 * Mount <MobileStyles /> once at the app root.
 */
exports.MOBILE_BREAKPOINT = 768;
exports.MOBILE_CSS = `
@media (max-width: ${exports.MOBILE_BREAKPOINT - 1}px) {
  body { overflow-x: hidden; }
  *, *::before, *::after { box-sizing: border-box; }
  body { overflow-wrap: break-word; }

  body *:not(td):not(th):not(tr):not(thead):not(tbody):not(tfoot):not(col):not(colgroup) {
    max-width: 100%;
  }
  img, video, iframe { height: auto; }

  /* Safety net: wide tables scroll inside their own card. */
  table:not(.stack-mobile) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100% !important;
  }
  table:not(.stack-mobile) td, table:not(.stack-mobile) th { white-space: nowrap; }

  /* Row-per-card tables. */
  table.stack-mobile, table.stack-mobile tbody, table.stack-mobile tr, table.stack-mobile td {
    display: block;
    width: 100% !important;
  }
  table.stack-mobile thead { display: none; }
  table.stack-mobile tr {
    margin-bottom: 12px;
    padding: 8px 12px;
    border: 1px solid rgba(128,128,128,0.3) !important;
    border-radius: 10px;
    background: rgba(128,128,128,0.06);
  }
  table.stack-mobile td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 6px 0 !important;
    text-align: right !important;
    white-space: normal !important;
    border: none !important;
  }
  table.stack-mobile td[data-label]::before {
    content: attr(data-label);
    font-weight: 600;
    opacity: 0.7;
    text-align: left;
    margin-right: auto;
  }
  table.stack-mobile td:empty { display: none; }
  /* A full-width detail row (e.g. an expanded "Edit access" panel) — no label, no right-align. */
  table.stack-mobile td[colspan] { display: block; text-align: left !important; }

  /* Fixed-column grids → one column. */
  [style*="grid-template-columns"]:not([style*="auto"]):not([style*="repeat(7"]):not([data-keep-grid]) {
    grid-template-columns: minmax(0, 1fr) !important; /* minmax(0,…) so wide content can't stretch it */
  }
  /* Auto-fit grids whose smallest column is wider than a phone (minmax(200px+)) → one column. */
  [style*="minmax(2"]:not([data-keep-grid]), [style*="minmax(3"]:not([data-keep-grid]),
  [style*="minmax(4"]:not([data-keep-grid]), [style*="minmax(5"]:not([data-keep-grid]) {
    grid-template-columns: minmax(0, 1fr) !important;
  }
  /* A desktop min-width (e.g. min-width: 300px on a side panel) mustn't push the page wider. */
  body *:not(td):not(th)[style*="min-width"] { min-width: 0 !important; }
  /* Rows of side-by-side items wrap instead of running off the screen. */
  [style*="display: flex"]:not([style*="nowrap"]):not([style*="column"]):not([data-keep-row]) {
    flex-wrap: wrap;
  }

  input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), select, textarea {
    font-size: 16px !important;
  }
  button, select, input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]) {
    min-height: 44px;
  }
  input[type="checkbox"], input[type="radio"] { width: 20px; height: 20px; }

  /* Keep a long form's Save/Add button on screen. */
  .sticky-actions {
    position: sticky;
    bottom: 72px;
    z-index: 5;
    padding: 10px 0;
    background: inherit;
  }

  /* Pop-ups (an overlay marked data-sheet, and the shared Modal) open as a bottom sheet: full
     width, rounded top, scrolls inside itself, clear of the iPhone home bar (2026-09-30). */
  [data-sheet] { align-items: flex-end !important; justify-content: stretch !important; padding: 0 !important; z-index: 10500 !important; }
  [data-sheet] > * {
    width: 100% !important;
    max-width: 100% !important;
    max-height: 92vh !important;
    overflow-y: auto !important;
    margin: 0 !important;
    border-radius: 18px 18px 0 0 !important;
    padding-bottom: calc(20px + env(safe-area-inset-bottom)) !important;
    animation: mango-sheet-up 180ms ease-out;
  }
  @keyframes mango-sheet-up { from { transform: translateY(40px); opacity: 0.6; } to { transform: none; opacity: 1; } }

  /* Anything marked desktop-only is hidden on a phone. */
  .hide-mobile { display: none !important; }
}
@media (min-width: ${exports.MOBILE_BREAKPOINT}px) {
  .show-mobile { display: none !important; }
}
`;
function MobileStyles() {
    return (0, jsx_runtime_1.jsx)("style", { "data-mobile-styles": true, children: exports.MOBILE_CSS });
}
exports.MobileStyles = MobileStyles;
/** True below MOBILE_BREAKPOINT (or the given width); updates on resize/rotate. */
function useIsMobile(breakpoint = exports.MOBILE_BREAKPOINT) {
    const query = `(max-width: ${breakpoint - 1}px)`;
    const [isMobile, setIsMobile] = (0, react_1.useState)(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
    (0, react_1.useEffect)(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const mql = window.matchMedia(query);
        const onChange = () => setIsMobile(mql.matches);
        onChange();
        mql.addEventListener?.('change', onChange);
        return () => mql.removeEventListener?.('change', onChange);
    }, [query]);
    return isMobile;
}
exports.useIsMobile = useIsMobile;
/**
 * Remembers a value for the rest of this browser session only. sessionStorage is wiped when the
 * tab/app is closed, so reopening the dashboard starts fresh, while moving between pages in the
 * same session keeps what you had.
 */
function useSessionState(key, initial) {
    const [value, setValue] = (0, react_1.useState)(() => {
        if (!key || typeof window === 'undefined')
            return initial;
        try {
            const raw = window.sessionStorage.getItem(key);
            return raw === null ? initial : JSON.parse(raw);
        }
        catch {
            return initial;
        }
    });
    const set = (next) => {
        setValue((prev) => {
            const resolved = typeof next === 'function' ? next(prev) : next;
            if (key && typeof window !== 'undefined') {
                try {
                    window.sessionStorage.setItem(key, JSON.stringify(resolved));
                }
                catch {
                    /* storage full/blocked — just don't remember */
                }
            }
            return resolved;
        });
    };
    return [value, set];
}
exports.useSessionState = useSessionState;
